import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Search, ListPlus, Save, Trash2, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Bit } from '../types/bit';
import { SetList as SetListType } from '../types/setlist';
import { SortableBitItem } from './setlist/SortableBitItem';
import { Timer } from './setlist/Timer';
import DeleteModal from './modals/DeleteModal';

export default function SetList() {
  const { user } = useAuth();
  const [bits, setBits] = useState<Bit[]>([]);
  const [selectedBits, setSelectedBits] = useState<Bit[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [editingSetList, setEditingSetList] = useState<string | null>(null);
  const [savedSetLists, setSavedSetLists] = useState<SetListType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState(5);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    title: string;
  }>({
    isOpen: false,
    id: '',
    title: '',
  });
  const [showFullSet, setShowFullSet] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const bitRefs = useRef<(HTMLDivElement | null)[]>([]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // Add a small delay and distance constraint to differentiate between scrolling and dragging
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const filteredBits = bits.filter(bit =>
    bit.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (bit.content || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (bit.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  useEffect(() => {
    if (!user?.uid) return;

    const fetchBits = async () => {
      try {
        const q = query(collection(db, 'drafts'), where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        console.log('Fetched bits:', snapshot.docs.length);
        const bitsData = snapshot.docs.map(doc => {
          const data = doc.data();
          const content = data.content || '';
          // Calculate estimated time based on word count (120 words per minute)
          const wordCount = content.trim().split(/\s+/).length;
          const rawMinutes = wordCount / 120;
          // Round to nearest 5 seconds (0.0833 minutes) for display purposes
          const estimatedTime = Math.round(rawMinutes * 12) / 12;

          return {
            id: doc.id,
            title: data.title,
            content: content,
            tags: data.tags || [],
            userId: data.userId,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            audioUrl: data.audioUrl || '',
            estimatedTime,
            wordCount
          } as Bit;
        });
        setBits(bitsData);
      } catch (error) {
        console.error('Error fetching bits:', error);
      }
    };

    const fetchSetLists = async () => {
      try {
        const q = query(collection(db, 'setlists'), where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        console.log('Fetched set lists:', snapshot.docs.length);
        const setListsData = snapshot.docs.map(doc => {
          const data = doc.data();
          // Recalculate estimated times for each bit using the new formula
          const bitsWithUpdatedTimes = data.bits.map((bit: Bit) => {
            const content = bit.content || '';
            const wordCount = content.trim().split(/\s+/).length;
            const rawMinutes = wordCount / 120;
            // Round to nearest 5 seconds (0.0833 minutes) for display purposes
            const estimatedTime = Math.round(rawMinutes * 12) / 12;
            return { ...bit, estimatedTime, wordCount };
          });
          return {
            id: doc.id,
            ...data,
            bits: bitsWithUpdatedTimes
          };
        }) as SetListType[];
        console.log('Set lists data:', setListsData);
        setSavedSetLists(setListsData);
      } catch (error) {
        console.error('Error fetching set lists:', error);
      }
    };

    fetchBits();
    fetchSetLists();
  }, [user?.uid]);

  const handleBitSelect = (bit: Bit) => {
    if (!selectedBits.some(selected => selected.id === bit.id)) {
      setSelectedBits([...selectedBits, bit]);
    }
  };

  const handleRemoveBit = (bitId: string) => {
    setSelectedBits(selectedBits.filter(bit => bit.id !== bitId));
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSelectedBits((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveSetList = async () => {
    try {
      if (!user?.uid) {
        console.error('No user found');
        return;
      }

      const setListData = {
        title: title.trim(),
        description: description.trim(),
        bits: selectedBits,
        duration: selectedDuration,
        userId: user.uid,
        updatedAt: new Date().toISOString()
      };

      if (editingSetList) {
        // Update existing set list
        const setListRef = doc(db, 'setlists', editingSetList);
        await updateDoc(setListRef, setListData);
        setSavedSetLists(prev => prev.map(list => 
          list.id === editingSetList ? { ...list, ...setListData } : list
        ));
      } else {
        // Create new set list
        const newSetListData = {
          ...setListData,
          createdAt: new Date().toISOString()
        };
        const setListsRef = collection(db, 'setlists');
        const docRef = await addDoc(setListsRef, newSetListData);
        setSavedSetLists(prev => [...prev, { ...newSetListData, id: docRef.id }]);
      }
      
      // Clear form
      setTitle('');
      setDescription('');
      setSelectedBits([]);
      setEditingSetList(null);
    } catch (error) {
      console.error('Error saving set list:', error);
    }
  };

  const handleEditSetList = (setList: SetListType) => {
    setEditingSetList(setList.id);
    setTitle(setList.title);
    setDescription(setList.description || '');
    setSelectedBits(setList.bits);
  };

  const handleDeleteSetList = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'setlists', id));
      setSavedSetLists(prev => prev.filter(list => list.id !== id));
      setDeleteModal({ isOpen: false, id: '', title: '' });
      if (editingSetList === id) {
        setEditingSetList(null);
        setTitle('');
        setDescription('');
        setSelectedBits([]);
      }
    } catch (error) {
      console.error('Error deleting set list:', error);
    }
  };

  const handleNewSetList = () => {
    setEditingSetList(null);
    setTitle('');
    setDescription('');
    setSelectedBits([]);
  };

  const getTotalDuration = () => {
    const total = selectedBits.reduce((acc, bit) => {
      // Round each bit's time to 1 decimal place before adding
      const roundedTime = Math.round(bit.estimatedTime * 10) / 10;
      return acc + roundedTime;
    }, 0);
    return total;
  };

  // Reset bit refs when bits change
  useEffect(() => {
    bitRefs.current = selectedBits.map(() => null);
  }, [selectedBits]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Timer Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col items-center gap-3">
            <Timer
              duration={selectedDuration * 60}
              isRunning={isRunning}
              setIsRunning={setIsRunning}
              onComplete={() => {}}
              onDurationChange={(newDuration) => setSelectedDuration(Math.floor(newDuration / 60))}
            />
            {selectedBits.length > 0 && (
              <div className={`text-sm ${getTotalDuration() > selectedDuration ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                Total set duration: {getTotalDuration().toFixed(1)} minutes
                {getTotalDuration() > selectedDuration && (
                  <span className="block mt-1">
                    ⚠️ Set is longer than selected time limit ({selectedDuration} minutes)
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column - Create New Set List */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Create New Set List</h2>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                placeholder="Set List Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />

              <button
                type="button"
                onClick={handleSaveSetList}
                disabled={!title.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={16} />
                {editingSetList ? 'Update Set List' : 'Save Set List'}
              </button>
            </div>
          </div>

          {/* Right Column - Saved Set Lists */}
          <div className="flex flex-col gap-4 mb-8 md:mb-0">
            <h2 className="text-lg font-semibold text-gray-900">Saved Set Lists</h2>
            
            <div className="space-y-4">
              <select
                className="w-full p-2 border-2 border-orange-500 rounded-lg bg-white text-gray-900 font-medium shadow-sm md:border md:border-gray-300"
                onChange={(e) => {
                  const setList = savedSetLists.find(list => list.id === e.target.value);
                  if (setList) {
                    setTitle(setList.title);
                    setDescription(setList.description || '');
                    setSelectedBits(setList.bits);
                    setSelectedDuration(setList.duration);
                    setEditingSetList(setList.id);
                  }
                }}
                value={editingSetList || ''}
              >
                <option value="">Select a set list...</option>
                {savedSetLists.map((setList) => {
                  const actualDuration = setList.bits.reduce((acc, bit) => acc + bit.estimatedTime, 0);
                  return (
                    <option key={setList.id} value={setList.id}>
                      {setList.title} ({setList.bits.length} bits, {actualDuration.toFixed(1)}min)
                    </option>
                  );
                })}
              </select>

              {/* Preview of selected set list */}
              {editingSetList && (
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {savedSetLists.find(list => list.id === editingSetList)?.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {savedSetLists.find(list => list.id === editingSetList)?.description}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingSetList(null);
                        setTitle('');
                        setDescription('');
                        setSelectedBits([]);
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full text-gray-500"
                    >
                      <X size={16} />
                    </button>
                    <button
                      onClick={() => {
                        if (editingSetList) {
                          setDeleteModal({
                            isOpen: true,
                            id: editingSetList,
                            title: savedSetLists.find(list => list.id === editingSetList)?.title || ''
                          });
                        }
                      }}
                      className="p-1 hover:bg-red-50 rounded-full text-gray-500 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* List of bits in the set */}
                <div className="space-y-2">
                  {savedSetLists.find(list => list.id === editingSetList)?.bits.map((bit, index) => (
                    <div key={bit.id} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="font-medium">{index + 1}.</span>
                      <span className="flex-1">{bit.title}</span>
                      <span className="text-gray-400">{bit.estimatedTime.toFixed(1)}min</span>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Selected Set List */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900">Set List</h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowFullSet(!showFullSet)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                      showFullSet 
                        ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                  >
                    {showFullSet ? 'Hide Full Set' : 'View Full Set'}
                  </button>
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    Total Time: {getTotalDuration().toFixed(1)}m
                  </span>
                </div>
              </div>
            </div>

            <div 
              className="p-3 overflow-y-auto relative" 
              ref={containerRef} 
              style={{ height: '600px' }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selectedBits.map((bit) => bit.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {selectedBits.map((bit, index) => (
                      <div 
                        key={bit.id}
                        className="space-y-2"
                      >
                        <SortableBitItem
                          bit={bit}
                          onRemove={handleRemoveBit}
                        />
                        {showFullSet && (
                          <div className="ml-8 p-3 bg-gray-50 rounded-md text-sm text-gray-700 whitespace-pre-wrap">
                            {bit.content}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {selectedBits.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-2">
                    <ListPlus size={32} className="mx-auto" />
                  </div>
                  <p className="text-gray-500 text-sm">
                    Select bits from Available Bits to build your set list
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Available Bits */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-3 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900">Available Bits</h2>
                <span className="text-sm text-gray-500">{bits.length} bits</span>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search bits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-2 pl-8 border border-gray-300 rounded-lg"
                />
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
            </div>
            
            <div className="overflow-y-auto" style={{ height: '600px' }}>
              <div className="divide-y divide-gray-100">
                {filteredBits.map((bit) => (
                  <div
                    key={bit.id}
                    onClick={() => handleBitSelect(bit)}
                    className="p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 group"
                  >
                    <div className="flex justify-between items-start mb-1.5">
                      <h3 className="font-semibold text-gray-900 group-hover:text-orange-500">
                        {bit.title}
                      </h3>
                      <div className="flex flex-col items-end text-sm text-gray-500">
                        <span className="flex items-center">
                          <Clock size={14} className="mr-1" />
                          {bit.estimatedTime.toFixed(1)}m
                        </span>
                        <span className="text-xs text-gray-400">
                          {bit.wordCount} words
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{bit.content}</p>
                    {bit.tags && bit.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {bit.tags.map(tag => (
                          <span
                            key={tag}
                            className="text-xs bg-gray-50 text-gray-600 px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        title={`Delete "${deleteModal.title}"?`}
        message="This action cannot be undone."
        onConfirm={() => handleDeleteSetList(deleteModal.id)}
        onClose={() => setDeleteModal({ isOpen: false, id: '', title: '' })}
      />
    </div>
  );
}
